import { useState, useEffect, useLayoutEffect } from 'react';

// Public pages are prerendered by react-snap at a fixed viewport
// (PRERENDER_WIDTH, pinned in client/package.json "reactSnap.viewport") and
// then hydrated in the browser. If the FIRST client render reads the real
// viewport, any browser on the other side of the breakpoint renders a
// different layout from the server HTML and React 19 reports a hydration
// mismatch (HOOPHERO-CLIENT-2). So: when the root already has server
// markup, start from the value the prerender used and switch to the real
// one in a layout effect, which runs before the browser paints.
// Non-hydrated renders (client-side navigation, dev server) read the
// viewport immediately as before.
export const PRERENDER_WIDTH = 480;
let hydrating; // decided once, on the first hook call (before hydrateRoot has committed)
const isHydrating = () => {
  if (hydrating === undefined) {
    const root = typeof document !== 'undefined' && document.getElementById('root');
    hydrating = !!(root && root.hasChildNodes());
  }
  return hydrating;
};
// Test-only: forget the cached decision.
export const __resetHydrationDetection = () => { hydrating = undefined; };

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Returns true when the viewport is at or below the given breakpoint.
 * Reactive — updates on resize / device-orientation change via matchMedia.
 *
 * Default breakpoint matches our Landing.css mobile threshold (640px).
 * Layouts use a slightly wider breakpoint (768px) so tablets in portrait
 * also use the drawer instead of fighting the 224px sidebar.
 */
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (isHydrating()) return PRERENDER_WIDTH <= breakpoint;
    return window.innerWidth <= breakpoint;
  });

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);

    // Sync immediately (before paint) so a hydrated page snaps to the real
    // viewport without a visible desktop flash.
    setIsMobile(mq.matches);

    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // Safari < 14 fallback
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [breakpoint]);

  return isMobile;
}

export default useIsMobile;
